import { Formatters, Messages } from '../../codegen/Localize';
import { IActionContext } from '../helpers/ActionContext';
import { ResultHelper } from '../helpers/ResultHelper';
import { L10n } from '../L10n/L10n';
import { DataAlertDefinition, DataRoleDefinition, DatasourceDefinition, FlowDefinition, MetricDefinition, ProjectDefinition, WorkbookDefinition,  } from '../models/ContentDefinitions';
import { FlowListItemInfo } from '../models/FlowInfo';
import { IContentInfo } from '../models/IContentInfo';
import { IDataAlertBase } from '../models/IDataAlertInfo';
import { IDataRoleInfo } from '../models/IDataRoleInfo';
import { IDataSourceBase } from '../models/IDataSourceInfo';
import { IMetricInfo } from '../models/IMetricInfo';
import { IProjectBase } from '../models/IProjectInfo';
import { IWorkbookBase } from '../models/IWorkbookInfo';
import { IErrorItem } from '../models/MultipleItemResult';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { DataAlerts } from '../resources/DataAlerts';
import { DataRoles } from '../resources/Dataroles';
import { DataSources } from '../resources/Datasources';
import { Flows } from '../resources/Flows';
import { Metrics } from '../resources/Metrics';
import { Projects } from '../resources/Projects';
import { Workbooks } from '../resources/Workbooks';
import { IResult } from '../ServerApi/types/IResponse';
import { IUserName } from '../ServerApi/types/IUser';
import { ActionHelpers } from './ActionHelpers';
import { ChangeDataAlertOwnerAction } from './ChangeDataAlertOwnerAction';
import { IResultWithTargetOwner } from './ChangeWorkbookOwnerAction';
import { ConfirmActionDialog, IConfirmActionDialogInstance } from './ConfirmActionDialog';
import { IConfirmChangeOwnerScope } from './ContentActions';
import { DataRoleErrors } from './DataRoleErrors';
import { DataSourceErrors } from './DataSourceErrors';
import { FlowErrors } from './FlowErrors';
import { MetricErrors } from './MetricErrors';
import { ProjectErrors } from './ProjectErrors';
import { SearchSuggestionsService } from './SearchSuggestionsService';
import { WorkbookErrors } from './WorkbookErrors';

export class ChangeContentOwnerAction {
  public static $inject = [
    '$q',
    'ActionHelpers',
    'ConfirmActionDialog',
    'DataRoleErrors',
    'DataRoles',
    'DataSourceErrors',
    'DataSources',
    'Flows',
    'FlowErrors',
    'ProjectErrors',
    'Projects',
    'SearchSuggestionsService',
    'WorkbookErrors',
    'Workbooks',
    'DataAlerts',
    'ChangeDataAlertOwnerAction',
    'Metrics',
    'MetricErrors',
  ];

  constructor(
    private $q: ng.IQService,
    private ActionHelpers: ActionHelpers,
    private ConfirmActionDialog: ConfirmActionDialog,
    private DataRoleErrors: DataRoleErrors,
    private DataRoles: DataRoles,
    private DataSourceErrors: DataSourceErrors,
    private DataSources: DataSources,
    private Flows: Flows,
    private FlowErrors: FlowErrors,
    private ProjectErrors: ProjectErrors,
    private Projects: Projects,
    private SearchSuggestionsService: SearchSuggestionsService,
    private WorkbookErrors: WorkbookErrors,
    private Workbooks: Workbooks,
    private DataAlerts: DataAlerts,
    private ChangeDataAlertOwnerAction: ChangeDataAlertOwnerAction,
    private Metrics: Metrics,
    private MetricErrors: MetricErrors,
  ) {}

  public confirmChangeOwner(content: IContentInfo[], context: IActionContext): ng.IPromise<IResultWithTargetOwner> {
    const ownerPicker = {
      prompt: getPromptForContent(content),
      ownerQuery: (matchText: string) => this.getQuery(content, matchText),
      targetOwner: <IUserName>null
    };

    const dialogScope: IConfirmChangeOwnerScope = context.scope.$new() as IConfirmChangeOwnerScope;
    dialogScope.ownerPicker = ownerPicker;

    const count = content.length;
    const dialogInstance = this.ConfirmActionDialog.open({
      scope: dialogScope,
      title: Messages.changeOwner(),
      bodyTemplateUrl: 'changeOwnerDialog.html',
      actionName: Messages.changeOwnerConfirm({ COUNT: count, COUNT_LOC: Formatters.formatCount(count) }),
      action: (_instance: IConfirmActionDialogInstance) => this.setOwnerForContent(content, ownerPicker.targetOwner),
      confirmDisabled: () => !ownerPicker.targetOwner || !ownerPicker.targetOwner.id
    });

    return dialogInstance.result
      .then((results: IResultWithTargetOwner[]) => ({ ...ResultHelper.combineResults(results), ownerId: dialogScope.ownerPicker.targetOwner.id }))
      .finally(() => dialogScope.$destroy());
  }

  private getQuery(content: IContentInfo[], matchText: string) {
    const containsProjects = content.find(i => i.contentType === ProjectDefinition.contentType) !== undefined;
    if (containsProjects) {
      return this.SearchSuggestionsService.usersCanOwnProject(matchText);
    }
    return this.SearchSuggestionsService.usersWithoutGuest(matchText);
  }

  private setOwnerForContent(content: IContentInfo[], owner: IUserName) {
    const projects = Set.fromIdItemArray(content.filter(i => i.contentType === ProjectDefinition.contentType));
    const workbooks = Set.fromIdItemArray(content.filter(i => i.contentType === WorkbookDefinition.contentType));
    const datasources = Set.fromIdItemArray(content.filter(i => i.contentType === DatasourceDefinition.contentType));
    const flows = Set.fromIdItemArray(content.filter(i => i.contentType === FlowDefinition.contentType));
    const dataroles = Set.fromIdItemArray(content.filter(i => i.contentType === DataRoleDefinition.contentType));
    const alerts = Set.fromIdItemArray(content.filter(i => i.contentType === DataAlertDefinition.itemType));
    const metrics = Set.fromIdItemArray(content.filter(i => i.contentType === MetricDefinition.itemType));

    return this.$q.all([
      this.setOwnerForProjects(projects, owner),
      this.setOwnerForWorkbooks(workbooks, owner),
      this.setOwnerForDatasources(datasources, owner),
      this.setOwnerForFlows(flows, owner),
      this.setOwnerForDataRoles(dataroles, owner),
      this.setOwnerForDataAlerts(alerts, owner),
      this.setOwnerForMetrics(metrics, owner),
    ]);
  }

  private setOwnerForProjects(projects: Set<IContentInfo>, owner: IUserName): ng.IPromise<IResult> {
    if (projects.size() < 1) {
      return this.$q.when(null);
    }

    return this.Projects.setOwner(projects.keys(), owner.id).then((result: IResult) => {
      return this.ActionHelpers.notifyStandardError(
        projects,
        { successId: 'changeOwnerProjectsSuccess', errorId: 'changeOwnerProjectsError' },
        (errorItem: IErrorItem<IProjectBase>) => this.ProjectErrors.errorDetailsFor(errorItem),
        { OWNER_NAME: owner.displayName }
      )(result);
    });
  }

  private setOwnerForMetrics(metrics: Set<IContentInfo>, owner: IUserName): ng.IPromise<IResult> {
    if (metrics.size() < 1) {
      return this.$q.when(null);
    }

    return this.Metrics.setOwner(metrics.keys(), owner.id).then((result: IResult) => {
      return this.ActionHelpers.notifyStandardError(
        metrics,
        { successId: 'changeOwnerForMetricsSuccess', errorId: 'changeOwnerForMetricsError' },
        (errorItem: IErrorItem<IMetricInfo>) => this.MetricErrors.errorDetailsFor(errorItem),
        { OWNER_NAME: owner.displayName }
      )(result);
    });
  }

  private setOwnerForWorkbooks(workbooks: Set<IContentInfo>, owner: IUserName): ng.IPromise<IResult> {
    if (workbooks.size() < 1) {
      return this.$q.when(null);
    }

    return this.Workbooks.setOwner(workbooks.keys(), owner.id).then((result: IResult) => {
      return this.ActionHelpers.notifyStandardError(
        workbooks,
        { successId: 'changeOwnerWorkbooksSuccess', errorId: 'changeOwnerWorkbooksError' },
        (errorItem: IErrorItem<IWorkbookBase>) => this.WorkbookErrors.errorDetailsFor(errorItem),
        { OWNER_NAME: owner.displayName }
      )(result);
    });
  }

  private setOwnerForDatasources(datasources: Set<IContentInfo>, owner: IUserName): ng.IPromise<IResult> {
    if (datasources.size() < 1) {
      return this.$q.when(null);
    }

    return this.DataSources.setOwner(datasources.keys(), owner.id).then((result: IResult) => {
      return this.ActionHelpers.notifyStandardError(
        datasources,
        { successId: 'changeOwnerDatasourcesSuccess', errorId: 'changeOwnerDatasourcesError' },
        (errorItem: IErrorItem<IDataSourceBase>) => this.DataSourceErrors.errorDetailsFor(errorItem),
        { OWNER_NAME: owner.displayName }
      )(result);
    });
  }

  private setOwnerForFlows(flows: Set<IContentInfo>, owner: IUserName): ng.IPromise<IResult> {
    if (flows.size() < 1) {
      return this.$q.when(null);
    }

    return this.Flows.setOwner(flows.keys(), owner.id).then((result: IResult) => {
      return this.ActionHelpers.notifyStandardError(
        flows,
        { successId: 'changeOwnerFlowsSuccess', errorId: 'changeOwnerFlowsError' },
        (errorItem: IErrorItem<FlowListItemInfo>) => this.FlowErrors.errorDetailsFor(errorItem),
        { OWNER_NAME: owner.displayName }
      )(result);
    });
  }

  private setOwnerForDataRoles(dataroles: Set<IContentInfo>, owner: IUserName): ng.IPromise<IResult> {
    if (dataroles.size() < 1) {
      return this.$q.when(null);
    }

    return this.DataRoles.setOwner(dataroles.keys(), owner.id).then((result: IResult) => {
      return this.ActionHelpers.notifyStandardError(
        dataroles,
        { successId: 'changeOwnerDataRolesSuccess', errorId: 'changeOwnerDataRolesError' },
        (errorItem: IErrorItem<IDataRoleInfo>) => this.DataRoleErrors.errorDetailsFor(errorItem),
        { OWNER_NAME: owner.displayName }
      )(result);
    });
  }

  private setOwnerForDataAlerts(alerts: Set<IContentInfo>, owner: IUserName): ng.IPromise<IResult> {
    if (alerts.size() < 1) {
      return this.$q.when(null);
    }

    return this.DataAlerts.setOwner(alerts.keys(), owner.id).then((result: IResult) => {
      return this.ActionHelpers.notifyStandardError<IDataAlertBase>(
        alerts as Set<IDataAlertBase>,
        { successId: 'changeOwnerDataAlertsSuccess', errorId: 'changeOwnerDataAlertsError' },
        (errorItem: IErrorItem<IDataAlertBase>) => this.ChangeDataAlertOwnerAction.errorDetailFor(errorItem),
        { OWNER_NAME: owner.displayName }
      )(result);
    });
  }
}

function getPromptForContent(content: IContentInfo[]): string {
  if (content.length === 1) {
    const item = content[0];
    return Messages.changeOwnerContentItemPrompt({ NAME: L10n.elideIfLong(item.name) });
  } else {
    return Messages.changeOwnerContentItemsPrompt({ COUNT_LOC: Formatters.formatCount(content.length) });
  }
}

Module.instance().service('ChangeContentOwnerAction', ChangeContentOwnerAction);
